import React, { useEffect, useState } from "react";
import { useTranslation } from "@me8eon/translation";
import { EonUiButton, EonUiIcon } from "@eon-ui/eon-ui-components-react";

import "./AllAppsComponent.scss";

import useSearchStore from "../../store/searchStore";
import LoaderComponent from "../loader-component/LoaderComponent";
import { ThemedIcon } from "@me8eon/icons";
import { EnvironmentConfig } from "@me8eon/config/src/EnvironmentConfig";
import { TileDataInterface } from "@me8eon/fav_storage";
import { decodeEntities } from "../../util/strings";
import { useJwtToken } from "@me8eon/react_authentication";
import NewTileComponent from "../tile-component/NewTileComponent";
import { SearchList } from "../search-list/SearchList";

type AllAppsProps = {
    toggleAllAppsModal: () => void;
}

type AppCardProps = {
    app: TileDataInterface;
}

const AppCard = ({ app }: AppCardProps) => {

    const {
        setPrefData,
        toggleFavourite,
    } = useSearchStore();
    const jwt = useJwtToken()

    const t = useTranslation();
    const isCustomIcon = app.icon?.endsWith(".svg");
    let translatedLabel = app.alias ? t(`appData.${app.alias}`) : app.title;
    if (translatedLabel.startsWith("appData.")) {
        translatedLabel = app.title;
    }

    const getIconName = (icon: string) => {
        if (icon.includes(".svg")) {
            const iconName = icon.split(".")[0];
            return iconName === "notification" ? "tools" : iconName;
        }
        return icon;
    }

    const onAddFavorite = (appData: any) => {
        toggleFavourite(appData, false);
        setPrefData(jwt);
    };

    return (
        <>
            <div className="icon">
                {!isCustomIcon ? (
                    <EonUiIcon
                        size="normal"
                        className="tile-icon eon-ui-svg-icon"
                        key={"tile-" + app.id}
                        name={app.icon}
                        scheme="red500"
                    ></EonUiIcon>
                ) : (
                    app.icon ? <ThemedIcon name={getIconName(app.icon)} /> : <></>
                )}
            </div>
            <div className="title-subtitle">
                <div className="title">{translatedLabel}</div>
                <div className="subtitle">{t(`appData.subTitle.${app.alias}SubTitle`).toUpperCase()}</div>
            </div>
            <div className="add-remove-icon">
                <EonUiIcon
                    name={app.status ? "subtract" : "add"}
                    scheme={app.status ? "red500" : "neutral800"}
                    className="add-icon"
                    size="small"
                    onClick={($event) => {
                        onAddFavorite(app);
                        $event.preventDefault();
                    }}
                    title={
                        app.status
                            ? t("searchPage.removeAppTooltip")
                            : t("searchPage.addAppTooltip")
                    }
                ></EonUiIcon>
            </div>
        </>
    );
};

const AllAppsComponent = ({ toggleAllAppsModal }: AllAppsProps) => {

    const esApiKey = useJwtToken();

    const [searchQuery, setSearchQuery] = useState<string>("");
    const t = useTranslation();

    const {
        favData,
        getAllApps,
        setAllApps,
        allAppsData,
    } = useSearchStore();
    const { favApps } = favData;

    useEffect(() => {
        getAllApps(searchQuery, esApiKey);
    }, [searchQuery]);

    useEffect(() => {
        const appsData = allAppsData.map((resp: any) => {
            return {
                ...resp,
                status: favApps.some(
                    (item) => item.alias === resp.alias
                ),
            };
        });
        setAllApps(appsData);
    }, [favData]);

    return (
        <div className="all-apps-container">
            <div className="heading">
                <div className="title">{t("allApps.modalHeading")}</div>
                <div className="description">{t("allApps.modalDesc")}</div>
            </div>
            <div className="search-box">
                <SearchList
                    queryChange={(value) => setSearchQuery(value)}
                    source="all-apps"
                    indices={[EnvironmentConfig.appIndex]}
                    placeholder={t("allApps.searchPlaceholder")}
                />
            </div>
            <div >
                <NewTileComponent toggleAllAppsModal={toggleAllAppsModal} />
            </div>
            <div className="apps-box">
                {allAppsData.map((app, key) => (
                    <div className="card" key={key} title={decodeEntities(t(`appData.${app.alias}hover`))}>
                        <AppCard app={app} />
                    </div>
                ))}
            </div>
            <div className="close-btn">
                <EonUiButton size="small" rank="primary" text={t("close")} onClick={toggleAllAppsModal}></EonUiButton>
            </div>
        </div>
    );
};

export default AllAppsComponent;